import React from 'react';
import { Chip } from '@mui/material';
import { Translate } from 'react-localize-redux';

interface AutoQcScoreChipProps {
    /** The device's auto-QC score on a 0-100 scale. Null when it reported none. */
    score: number | null | undefined;
}

/**
 * SKIN-240 — the device's auto-QC score for one image.
 *
 * **Raw value, no colour.** The colour-by-threshold logic that lives in
 * `ImageWithMeta` (`EncryptedImage.tsx`) is deliberately not carried over here. The
 * auto-QC is known to score good photographs badly, and one purpose of this feature is
 * to have reviewers judge on real images whether it works at all — so the number has to
 * be visible for them to disagree with it, and must not be pre-judged by a colour. No
 * threshold has been agreed; the 70/40 cuts used on the clinical submission view were
 * never validated. That view keeps its coloured chip: this is a different surface with
 * a different question, and changing it is not this ticket's to change.
 *
 * **The scale is 0-100.** The design handoff's 0-1 with a 0.6 threshold is wrong — the
 * device sends a `double precision` in 0-100 (production samples: 78.0, 82.5).
 *
 * A null score renders nothing rather than `0/100`: the API stores the column nullable
 * on purpose, so that a legacy upload and a value rejected as out-of-range both read as
 * "no opinion" instead of "a bad image".
 */
export default function AutoQcScoreChip({ score }: AutoQcScoreChipProps) {
    if (score === null || score === undefined || !Number.isFinite(score)) return null;

    // Scores are doubles and arrive fractional (82.5). Trailing ".0" is noise on a
    // chip, so an integral value renders bare while a fractional one keeps one decimal.
    const rendered = Number.isInteger(score) ? String(score) : score.toFixed(1);

    return (
        <Chip
            size="small"
            variant="outlined"
            data-testid="auto-qc-score"
            data-score={score}
            label={
                <React.Fragment>
                    <Translate id="hospital.quality-check.review.auto-qc" />
                    {` ${rendered}/100`}
                </React.Fragment>
            }
        />
    );
}
